import { ReactElement } from 'react';
import { useDeviceType } from '@client/common/hooks';
import { isPhone } from '@client/common/utils/env';
import classNames from 'classnames';
import { useSelectable } from 'b2b-components/hooks/useSelectable';
import { Header } from './Header';
import { Product } from './Product';
import { Props } from './';
import './Cart.scss';

export const ROW_CLASS_NAME = 'Cart-Row';
export const CELL_CLASS_NAME = 'Cart-Cell';
export const ROW_CLASS_NAMES = {
  header: `${ROW_CLASS_NAME}--Header`,
  withSelect: `${ROW_CLASS_NAME}--WithSelect`,
  withActions: `${ROW_CLASS_NAME}--WithActions`,
  withDiscount: `${ROW_CLASS_NAME}--WithDiscount`,
  withChildren: `${ROW_CLASS_NAME}--WithChildren`,
  mobile: `${ROW_CLASS_NAME}--Mobile`,
  incompleteData: `${ROW_CLASS_NAME}--IncompleteData`,
};
export const CELL_CLASS_NAMES = {
  select: `${CELL_CLASS_NAME}--Select`,
  thumbnail: `${CELL_CLASS_NAME}--Thumbnail`,
  title: `${CELL_CLASS_NAME}--Title`,
  stock: `${CELL_CLASS_NAME}--Stock`,
  price: `${CELL_CLASS_NAME}--Price`,
  quantity: `${CELL_CLASS_NAME}--Quantity`,
  total: `${CELL_CLASS_NAME}--Total`,
  actions: `${CELL_CLASS_NAME}--Actions`,
  buttons: `${CELL_CLASS_NAME}--Buttons`,
  details: `${CELL_CLASS_NAME}--Details`,
  children: `${CELL_CLASS_NAME}--Children`,
  reference: `${CELL_CLASS_NAME}--Reference`,
  discount: `${CELL_CLASS_NAME}--Discount`,
  discountLabel: `${CELL_CLASS_NAME}--DiscountLabel`,
  incompleteData: `${CELL_CLASS_NAME}--IncompleteData`,
};

export const Cart = ({
  products,
  showEmptyReferenceField,
  selectedProducts,
  isProductAddToCompareEnabled,
  hasActions,
  isEditable,
  isProductReferenceReadOnly,
  isWishlistPage = false,
  hasBorder = true,
  hasConfigurableItemsEnabled,
  onProductOpen,
  onProductAddToCart,
  onProductQuantityChange,
  onProductRemove,
  onProductReferenceChange,
  onSelectedProductsChange,
  onProductReferenceBlur,
  className,
  headerClassName,
  productClassName,
}: Props): ReactElement => {
  const isSelectable = Array.isArray(selectedProducts);
  const skus = products.map(item => item.sku);
  const deviceType = useDeviceType();
  const isMobile = isPhone(deviceType);

  const { onOptionChange, isOptionChecked } = useSelectable(
    undefined,
    selectedProducts,
    onSelectedProductsChange,
    true,
  );

  const commonClassName = classNames(
    ROW_CLASS_NAME,
    {
      [ROW_CLASS_NAMES.withSelect]: isSelectable,
      [ROW_CLASS_NAMES.withActions]: hasActions,
      [ROW_CLASS_NAMES.mobile]: isMobile,
    },
  );

  return (
    <ul className={classNames(
      'Cart',
      { 'Cart--Bordered': hasBorder },
      className,
    )}>
      <Header
        skus={skus}
        isSelectable={isSelectable}
        selectedSkus={selectedProducts}
        onCheckboxCheck={onSelectedProductsChange}
        className={classNames(commonClassName, ROW_CLASS_NAMES.header, headerClassName)}
      />

      {products.map((product, index) => (
        <Product
          product={product}
          showEmptyReferenceField={showEmptyReferenceField}
          hasActions={hasActions}
          hasConfigurableItemsEnabled={hasConfigurableItemsEnabled}
          listPosition={index + 1}
          isSelectable={isSelectable}
          isEditable={isEditable}
          isAddToCompareEnabled={isProductAddToCompareEnabled}
          isReferenceReadOnly={isProductReferenceReadOnly}
          isWishlistPage={isWishlistPage}
          getIsSelected={isOptionChecked}
          onOpen={onProductOpen}
          onAddToCart={onProductAddToCart}
          onSelect={onOptionChange}
          onRemove={onProductRemove}
          onQuantityChange={onProductQuantityChange}
          onReferenceBlur={onProductReferenceBlur}
          onReferenceChange={onProductReferenceChange}
          key={`${product.sku}${product.supplierConfig?.partAuxiliaryId || ''}`}
          className={classNames('Cart-Product', commonClassName, productClassName)}
        />
      ))}
    </ul>
  );
};
